import axios from 'axios';
import { z } from 'zod';
import {
  analysisBatchDetailSchema,
  analysisBatchRequestSchema,
  abstractExtractionResultSchema,
  aiResponseSchema,
  createTriageFieldTemplateSchema,
  extractFieldBatchDetailSchema,
  extractFieldBatchRequestSchema,
  fieldDecisionCardSchema,
  recordSchema,
  recordPublicationTypeFacetsResponseSchema,
  listResponseSchema,
  projectSchema,
  projectSearchDeletePreviewSchema,
  projectSearchRunSchema,
  createProjectSearchResponseSchema,
  createPubmedTextQueryTemplateSchema,
  initPdfOcrBatchRequestSchema,
  initPdfOcrBatchResponseSchema,
  runPdfOcrTaskRequestSchema,
  pdfOcrBatchDetailSchema,
  pdfOcrTaskDetailResponseSchema,
  pdfOcrTaskResponseSchema,
  triageFieldTemplateSchema,
  pubmedTextQueryTemplateSchema,
  saveRecordReviewRequestSchema,
  saveRecordReviewResponseSchema,
  createTriageExtractionSnapshotSchema,
  createTriageExtractionSnapshotResponseSchema,
  createTriageAnalysisSnapshotSchema,
  createTriageAnalysisSnapshotResponseSchema,
  appendTriageAnalysisBatchSchema,
  appendTriageAnalysisBatchResponseSchema,
  adminDashboardOverviewSchema,
  adminDashboardUsersResponseSchema,
  adminDashboardUsageSchema,
  userMenuUsageSummarySchema,
  platformCredentialViewSchema,
  aiRuntimeSettingsSchema,
  aiRuntimePricingSchema,
  updateAiRuntimeSettingsSchema,
  adminDashboardStorageSchema,
  adminDashboardWindowSchema,
  ncbiPlatformConfigSchema,
  updateTriageExtractionBatchSchema,
  listUserTriageTemplatesQuerySchema,
  listUserTriageTemplatesResponseSchema,
  userTriageTemplateDetailSchema,
  updatePubmedTextQueryTemplateSchema,
  updateTriageFieldTemplateSchema,
} from '@pubmed-seq-reader/shared/schemas';
import type {
  AIResponse,
  AnalysisBatchDetail,
  AnalysisBatchRequest,
  AbstractExtractionResult,
  CreateTriageFieldTemplateRequest,
  CreateProjectRequest,
  CreateProjectSearchRequest,
  CreateProjectSearchResponse,
  CreatePubmedTextQueryTemplateRequest,
  CreateRecordRequest,
  ExtractFieldBatchDetail,
  ExtractFieldBatchRequest,
  FieldDecisionCard,
  GenerateAIRequest,
  InitPdfOcrBatchRequest,
  InitPdfOcrBatchResponse,
  ListResponse,
  ListQuery,
  PdfOcrBatchDetail,
  PdfOcrTask,
  RunPdfOcrTaskRequest,
  PdfOcrTaskResponse,
  Project,
  ProjectSearchDeletePreview,
  ProjectSearchRun,
  PubmedTextQueryTemplate,
  Record as RecordType,
  StructuredFieldsV2Request,
  SaveRecordReviewRequest,
  SaveRecordReviewResponse,
  CreateTriageExtractionSnapshotRequest,
  CreateTriageExtractionSnapshotResponse,
  CreateTriageAnalysisSnapshotRequest,
  CreateTriageAnalysisSnapshotResponse,
  AppendTriageAnalysisBatchRequest,
  AppendTriageAnalysisBatchResponse,
  AdminDashboardOverview,
  AdminDashboardUsersResponse,
  AdminDashboardUsage,
  UserMenuUsageSummary,
  PlatformCredentialView,
  AiRuntimeSettings,
  AiRuntimePricing,
  NcbiPlatformConfig,
  AdminDashboardStorage,
  AdminDashboardWindow,
  TriageFieldTemplate,
  ListUserTriageTemplatesQuery,
  ListUserTriageTemplatesResponse,
  UserTriageTemplateDetail,
  UpdateTriageExtractionBatchRequest,
  UpdateTriageFieldTemplateRequest,
  UpdateProjectRequest,
  UpdatePubmedTextQueryTemplateRequest,
  UpdateRecordRequest,
} from '@pubmed-seq-reader/shared/schemas';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:3000'
    : typeof window !== 'undefined'
      ? window.location.origin
      : 'http://localhost:3000');

const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const adminInvitationCodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  isUsed: z.boolean(),
  expiresAt: z.coerce.date().nullable(),
  usedAt: z.coerce.date().nullable(),
  usedByUserId: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export type AdminInvitationCode = z.infer<typeof adminInvitationCodeSchema>;

const API_FIELD_LABELS: Record<string, string> = {
  limit: '分页大小',
  page: '页码',
  pageSize: '每页条数',
  count: '数量',
  expiresInDays: '过期天数',
  retmax: 'PubMed返回上限',
  totalFiles: '文件数量',
  perKeyConcurrency: '并发数',
  ocrPerKeyConcurrency: 'OCR并发数',
  tokenPriceCnyPerMillion: '每百万 Token 价格（CNY）',
};

function toApiPayload(data: unknown): { error?: unknown; message?: unknown; code?: unknown; details?: unknown } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const payload = data as {
    error?: unknown;
    message?: unknown;
    code?: unknown;
    details?: unknown;
  };
  return payload;
}

function formatApiFieldName(field: string) {
  const label = API_FIELD_LABELS[field];
  return label ? `${field}（${label}）` : field;
}

function translateConstraintMessage(raw: string) {
  const message = raw.trim();
  if (!message) return '';

  let match = message.match(/expected number to be <=\s*([+-]?\d+(?:\.\d+)?)/i);
  if (match) {
    return `超过上限，最大允许 ${match[1]}`;
  }

  match = message.match(/expected number to be >=\s*([+-]?\d+(?:\.\d+)?)/i);
  if (match) {
    return `低于下限，最小允许 ${match[1]}`;
  }

  match = message.match(/expected string to have <=\s*(\d+)\s*characters?/i);
  if (match) {
    return `长度超过上限，最多 ${match[1]} 个字符`;
  }

  match = message.match(/expected string to have >=\s*(\d+)\s*characters?/i);
  if (match) {
    return `长度低于下限，至少 ${match[1]} 个字符`;
  }

  match = message.match(/at most (\d+) characters?/i);
  if (match) {
    return `长度超过上限，最多 ${match[1]} 个字符`;
  }

  match = message.match(/at least (\d+) characters?/i);
  if (match) {
    return `长度低于下限，至少 ${match[1]} 个字符`;
  }

  if (/expected number/i.test(message)) return '类型不正确，应为数字';
  if (/expected string/i.test(message)) return '类型不正确，应为字符串';
  if (/invalid enum/i.test(message)) return '取值不在允许范围内';

  return message;
}

function collectValidationHints(details: unknown): string[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return [];
  const parsed = details as { formErrors?: unknown; fieldErrors?: unknown };
  const hints: string[] = [];

  if (parsed.fieldErrors && typeof parsed.fieldErrors === 'object' && !Array.isArray(parsed.fieldErrors)) {
    for (const [field, value] of Object.entries(parsed.fieldErrors as Record<string, unknown>)) {
      if (!Array.isArray(value)) continue;
      const translated = value
        .filter((item): item is string => typeof item === 'string')
        .map(item => translateConstraintMessage(item))
        .filter(Boolean);
      if (translated.length === 0) continue;
      const deduped = Array.from(new Set(translated));
      hints.push(`${formatApiFieldName(field)}：${deduped.join('；')}`);
    }
  }

  if (Array.isArray(parsed.formErrors)) {
    for (const item of parsed.formErrors) {
      if (typeof item !== 'string') continue;
      const translated = translateConstraintMessage(item);
      if (translated) hints.push(translated);
    }
  }

  return hints;
}

function buildFriendlyErrorMessage(status: number | undefined, payload: ReturnType<typeof toApiPayload>, fallback: string) {
  const code = typeof payload?.code === 'string' ? payload.code : '';
  const serverMessage = [payload?.error, payload?.message].find(
    item => typeof item === 'string' && item.trim().length > 0
  ) as string | undefined;
  const normalizedServerMessage = serverMessage?.trim() || '';

  if (code === 'VALIDATION_ERROR' || status === 400) {
    const hints = collectValidationHints(payload?.details);
    if (hints.length > 0) {
      return `请求参数有误：${hints.join('；')}`;
    }
    if (serverMessage) {
      return `请求参数有误：${serverMessage.trim()}`;
    }
    return '请求参数有误，请检查输入后重试。';
  }

  if (status === 401) return '登录状态已失效，请重新登录后重试。';
  if (status === 403) return '你没有权限执行该操作。';
  if (status === 404) return normalizedServerMessage ? `请求资源不存在：${normalizedServerMessage}` : '请求资源不存在或已被删除。';
  if (status === 429) return '请求过于频繁，请稍后重试。';
  if (typeof status === 'number' && status >= 500) {
    if (normalizedServerMessage && !/^failed to\b|^internal server error\b/i.test(normalizedServerMessage)) {
      return `服务器处理失败：${normalizedServerMessage}`;
    }
    return `服务器处理失败（${status}），请稍后重试。`;
  }

  if (normalizedServerMessage) return normalizedServerMessage;
  return fallback;
}

async function buildFriendlyFetchErrorMessage(response: Response, fallbackPrefix: string) {
  const raw = await response.text().catch(() => '');
  let payload: ReturnType<typeof toApiPayload> = null;
  if (raw) {
    try {
      payload = toApiPayload(JSON.parse(raw));
    } catch {
      payload = null;
    }
  }
  const fallback = raw.trim() || `${fallbackPrefix}: ${response.status} ${response.statusText}`;
  return buildFriendlyErrorMessage(response.status, payload, fallback);
}

apiClient.interceptors.response.use(
  response => response,
  error => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }
    const status = error.response?.status;
    const payload = toApiPayload(error.response?.data);
    error.message = buildFriendlyErrorMessage(status, payload, error.message || '请求失败');
    return Promise.reject(error);
  }
);

// Projects API
export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const response = await apiClient.get('/api/projects');
    return projectSchema.array().parse(response.data);
  },

  get: async (id: string): Promise<Project> => {
    const response = await apiClient.get(`/api/projects/${id}`);
    return projectSchema.parse(response.data);
  },

  create: async (data: CreateProjectRequest): Promise<Project> => {
    const response = await apiClient.post('/api/projects', data);
    return projectSchema.parse(response.data);
  },

  update: async (id: string, data: UpdateProjectRequest): Promise<Project> => {
    const response = await apiClient.put(`/api/projects/${id}`, data);
    return projectSchema.parse(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/projects/${id}`);
  },
};

export const projectSearchesApi = {
  list: async (
    projectId: string,
    limit = 200,
    options?: { kind?: ProjectSearchRun['kind'] | Array<ProjectSearchRun['kind']> }
  ): Promise<ProjectSearchRun[]> => {
    const kind = options?.kind
      ? Array.isArray(options.kind)
        ? options.kind.join(',')
        : options.kind
      : undefined;
    const response = await apiClient.get(`/api/projects/${projectId}/searches`, {
      params: {
        limit,
        ...(kind ? { kind } : {}),
      },
    });
    return projectSearchRunSchema.array().parse(response.data);
  },

  create: async (
    projectId: string,
    data: CreateProjectSearchRequest
  ): Promise<CreateProjectSearchResponse> => {
    const response = await apiClient.post(`/api/projects/${projectId}/searches`, data);
    return createProjectSearchResponseSchema.parse(response.data);
  },

  rename: async (projectId: string, searchId: string, name: string): Promise<ProjectSearchRun> => {
    const response = await apiClient.patch(`/api/projects/${projectId}/searches/${searchId}`, {
      name,
    });
    return projectSearchRunSchema.parse(response.data);
  },

  delete: async (projectId: string, searchId: string): Promise<void> => {
    await apiClient.delete(`/api/projects/${projectId}/searches/${searchId}`);
  },

  getDeletePreview: async (projectId: string, searchId: string): Promise<ProjectSearchDeletePreview> => {
    const response = await apiClient.get(`/api/projects/${projectId}/searches/${searchId}/delete-preview`);
    return projectSearchDeletePreviewSchema.parse(response.data);
  },
};

export const projectPdfOcrApi = {
  initBatch: async (projectId: string, data: InitPdfOcrBatchRequest): Promise<InitPdfOcrBatchResponse> => {
    const parsedPayload = initPdfOcrBatchRequestSchema.parse(data);
    const response = await apiClient.post(`/api/projects/${projectId}/pdf-ocr/init-batch`, parsedPayload);
    return initPdfOcrBatchResponseSchema.parse(response.data);
  },

  getBatch: async (
    projectId: string,
    runId: string,
    options?: {
      includePreview?: boolean;
    }
  ): Promise<PdfOcrBatchDetail> => {
    const response = await apiClient.get(`/api/projects/${projectId}/pdf-ocr/batches/${runId}`, {
      params: {
        ...(options?.includePreview ? { includePreview: true } : {}),
      },
    });
    const parsed = initPdfOcrBatchResponseSchema.parse(response.data);
    return pdfOcrBatchDetailSchema.parse(parsed.batch);
  },

  getTask: async (
    projectId: string,
    taskId: string,
    options?: {
      includePreview?: boolean;
    }
  ): Promise<PdfOcrTask> => {
    const response = await apiClient.get(`/api/projects/${projectId}/pdf-ocr/tasks/${taskId}`, {
      params: {
        ...(options?.includePreview !== false ? { includePreview: true } : {}),
      },
    });
    return pdfOcrTaskDetailResponseSchema.parse(response.data).task;
  },

  runTask: async (
    projectId: string,
    input: RunPdfOcrTaskRequest & {
      signal?: AbortSignal;
    }
  ): Promise<PdfOcrTaskResponse> => {
    const payload = runPdfOcrTaskRequestSchema.parse(input);
    const response = await apiClient.post(`/api/projects/${projectId}/pdf-ocr/task`, payload, { signal: input.signal });

    return pdfOcrTaskResponseSchema.parse(response.data);
  },

  retryTask: async (projectId: string, taskId: string): Promise<PdfOcrTaskResponse> => {
    const response = await apiClient.post(`/api/projects/${projectId}/pdf-ocr/tasks/${taskId}/retry`);
    return pdfOcrTaskResponseSchema.parse(response.data);
  },

  deleteTask: async (
    projectId: string,
    taskId: string,
    options?: {
      force?: boolean;
      deleteRecord?: boolean;
    }
  ): Promise<PdfOcrBatchDetail> => {
    const response = await apiClient.delete(`/api/projects/${projectId}/pdf-ocr/tasks/${taskId}`, {
      params: {
        ...(options?.force ? { force: true } : {}),
        ...(options?.deleteRecord ? { deleteRecord: true } : {}),
      },
    });
    const parsed = initPdfOcrBatchResponseSchema.parse(response.data);
    return pdfOcrBatchDetailSchema.parse(parsed.batch);
  },
};

export const pubmedTextQueryTemplatesApi = {
  list: async (limit = 200): Promise<PubmedTextQueryTemplate[]> => {
    const response = await apiClient.get('/api/pubmed-text-query-templates', {
      params: { limit },
    });
    return pubmedTextQueryTemplateSchema.array().parse(response.data);
  },

  create: async (data: CreatePubmedTextQueryTemplateRequest): Promise<PubmedTextQueryTemplate> => {
    const parsedPayload = createPubmedTextQueryTemplateSchema.parse(data);
    const response = await apiClient.post('/api/pubmed-text-query-templates', parsedPayload);
    return pubmedTextQueryTemplateSchema.parse(response.data);
  },

  getById: async (templateId: string): Promise<PubmedTextQueryTemplate> => {
    const response = await apiClient.get(`/api/pubmed-text-query-templates/${templateId}`);
    return pubmedTextQueryTemplateSchema.parse(response.data);
  },

  update: async (
    templateId: string,
    data: UpdatePubmedTextQueryTemplateRequest
  ): Promise<PubmedTextQueryTemplate> => {
    const parsedPayload = updatePubmedTextQueryTemplateSchema.parse(data);
    const response = await apiClient.put(`/api/pubmed-text-query-templates/${templateId}`, parsedPayload);
    return pubmedTextQueryTemplateSchema.parse(response.data);
  },

  delete: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/api/pubmed-text-query-templates/${templateId}`);
  },
};

export const adminApi = {
  listInvitationCodes: async (limit = 200): Promise<AdminInvitationCode[]> => {
    const response = await apiClient.get('/api/admin/invitation-codes', {
      params: { limit },
    });
    return adminInvitationCodeSchema.array().parse(response.data);
  },

  createInvitationCodes: async (payload: {
    count?: number;
    expiresInDays?: number;
    note?: string;
    codes?: string[];
  }): Promise<{ items: AdminInvitationCode[] }> => {
    const response = await apiClient.post('/api/admin/invitation-codes', payload);
    return z.object({ items: adminInvitationCodeSchema.array() }).parse(response.data);
  },

  revokeInvitationCode: async (id: string): Promise<{ item: AdminInvitationCode }> => {
    const response = await apiClient.post(`/api/admin/invitation-codes/${id}/revoke`);
    return z.object({ item: adminInvitationCodeSchema }).parse(response.data);
  },

  deleteInvitationCode: async (id: string): Promise<{ item: AdminInvitationCode }> => {
    const response = await apiClient.delete(`/api/admin/invitation-codes/${id}`);
    return z.object({ item: adminInvitationCodeSchema }).parse(response.data);
  },

  clearUnusedInvitationCodes: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/api/admin/invitation-codes/clear-unused');
    return z.object({ deletedCount: z.number().int().nonnegative() }).parse(response.data);
  },
};

export const accountApi = {
  getUserMenuUsageSummary: async (): Promise<UserMenuUsageSummary> => {
    const response = await apiClient.get('/api/usage/me/summary');
    return userMenuUsageSummarySchema.parse(response.data);
  },
};

export const adminDashboardApi = {
  getOverview: async (window: AdminDashboardWindow = '30d'): Promise<AdminDashboardOverview> => {
    const parsedWindow = adminDashboardWindowSchema.parse(window);
    const response = await apiClient.get('/api/admin/dashboard/overview', {
      params: { window: parsedWindow },
    });
    return adminDashboardOverviewSchema.parse(response.data);
  },

  listUsers: async (params?: {
    page?: number;
    pageSize?: number;
    query?: string;
    status?: 'active' | 'suspended' | 'all';
  }): Promise<AdminDashboardUsersResponse> => {
    const response = await apiClient.get('/api/admin/dashboard/users', {
      params: {
        ...(params?.page ? { page: params.page } : {}),
        ...(params?.pageSize ? { pageSize: params.pageSize } : {}),
        ...(params?.query ? { query: params.query } : {}),
        ...(params?.status ? { status: params.status } : {}),
      },
    });
    return adminDashboardUsersResponseSchema.parse(response.data);
  },

  suspendUser: async (userId: string, reason?: string): Promise<{ user: { id: string; isSuspended: boolean; suspendedAt: Date | null; suspendedReason: string | null } }> => {
    const response = await apiClient.post(`/api/admin/dashboard/users/${userId}/suspend`, {
      ...(reason?.trim() ? { reason: reason.trim() } : {}),
    });
    return z
      .object({
        user: z.object({
          id: z.string(),
          isSuspended: z.boolean(),
          suspendedAt: z.coerce.date().nullable(),
          suspendedReason: z.string().nullable(),
        }),
      })
      .parse(response.data);
  },

  resumeUser: async (userId: string): Promise<{ user: { id: string; isSuspended: boolean } }> => {
    const response = await apiClient.post(`/api/admin/dashboard/users/${userId}/resume`);
    return z
      .object({
        user: z.object({
          id: z.string(),
          isSuspended: z.boolean(),
        }),
      })
      .parse(response.data);
  },

  deleteUser: async (userId: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.delete(`/api/admin/dashboard/users/${userId}`);
    return z.object({ ok: z.boolean() }).parse(response.data);
  },

  resetUserPassword: async (
    userId: string,
    newPassword?: string
  ): Promise<{ user: { id: string }; temporaryPassword: string }> => {
    const response = await apiClient.post(`/api/admin/dashboard/users/${userId}/reset-password`, {
      ...(newPassword?.trim() ? { newPassword: newPassword.trim() } : {}),
    });
    return z
      .object({
        user: z.object({
          id: z.string(),
        }),
        temporaryPassword: z.string(),
      })
      .parse(response.data);
  },

  resetCurrentAdminPassword: async (
    newPassword?: string
  ): Promise<{ user: { id: string; email: string }; temporaryPassword: string; forceRelogin: boolean }> => {
    const response = await apiClient.post('/api/admin/dashboard/admin/reset-password', {
      ...(newPassword?.trim() ? { newPassword: newPassword.trim() } : {}),
    });
    return z
      .object({
        user: z.object({
          id: z.string(),
          email: z.string(),
        }),
        temporaryPassword: z.string(),
        forceRelogin: z.boolean(),
      })
      .parse(response.data);
  },

  getUserUsage: async (userId: string, window: AdminDashboardWindow = '30d'): Promise<AdminDashboardUsage> => {
    const parsedWindow = adminDashboardWindowSchema.parse(window);
    const response = await apiClient.get(`/api/admin/dashboard/users/${userId}/usage`, {
      params: { window: parsedWindow },
    });
    return adminDashboardUsageSchema.parse(response.data);
  },

  listKeys: async (): Promise<{ items: PlatformCredentialView[] }> => {
    const response = await apiClient.get('/api/admin/dashboard/keys');
    return z.object({ items: z.array(platformCredentialViewSchema) }).parse(response.data);
  },

  getAiRuntime: async (): Promise<{ item: AiRuntimeSettings }> => {
    const response = await apiClient.get('/api/admin/dashboard/ai-runtime');
    return z.object({ item: aiRuntimeSettingsSchema }).parse(response.data);
  },

  updateAiRuntime: async (payload: {
    defaultModel?: string;
    perKeyConcurrency?: number;
    ocrDefaultModel?: string;
    ocrPerKeyConcurrency?: number;
    tokenPriceCnyPerMillion?: number;
  }): Promise<{ item: AiRuntimeSettings }> => {
    const parsedPayload = updateAiRuntimeSettingsSchema.parse(payload);
    const response = await apiClient.put('/api/admin/dashboard/ai-runtime', parsedPayload);
    return z.object({ item: aiRuntimeSettingsSchema }).parse(response.data);
  },

  updateKey: async (
    scope: 'ai' | 'ocr',
    payload: {
      modelName: string;
      provider?: string | null;
      baseUrl?: string | null;
      secretText: string;
      secretEncoding?: 'plain' | 'base64';
      isActive?: boolean;
    }
  ): Promise<{ item: PlatformCredentialView }> => {
    const response = await apiClient.put(`/api/admin/dashboard/keys/${scope}`, payload);
    return z.object({ item: platformCredentialViewSchema }).parse(response.data);
  },

  deleteKey: async (scope: 'ai' | 'ocr', id: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.delete(`/api/admin/dashboard/keys/${scope}/${id}`);
    return z.object({ ok: z.boolean() }).parse(response.data);
  },

  getNcbiConfig: async (): Promise<{ item: NcbiPlatformConfig }> => {
    const response = await apiClient.get('/api/admin/dashboard/ncbi-config');
    return z.object({ item: ncbiPlatformConfigSchema }).parse(response.data);
  },

  updateNcbiConfig: async (payload: {
    email?: string | null;
    toolName?: string | null;
    retmax?: number | null;
    secretText: string;
    secretEncoding?: 'plain' | 'base64';
  }): Promise<{ item: NcbiPlatformConfig }> => {
    const response = await apiClient.put('/api/admin/dashboard/ncbi-config', payload);
    return z.object({ item: ncbiPlatformConfigSchema }).parse(response.data);
  },

  getStorage: async (): Promise<AdminDashboardStorage> => {
    const response = await apiClient.get('/api/admin/dashboard/storage');
    return adminDashboardStorageSchema.parse(response.data);
  },
};

// AI API
export const aiApi = {
  getRuntimePricing: async (): Promise<AiRuntimePricing> => {
    const response = await apiClient.get('/api/ai/runtime-pricing');
    return aiRuntimePricingSchema.parse(response.data);
  },

  listConfiguredModels: async (): Promise<string[]> => {
    const response = await apiClient.get('/api/ai/configured-models');
    return z
      .object({
        items: z.array(z.string()),
      })
      .parse(response.data).items;
  },

  generate: async (request: GenerateAIRequest): Promise<AIResponse> => {
    const response = await apiClient.post('/api/ai/generate', request);
    return aiResponseSchema.parse(response.data);
  },

  stream: async (
    request: GenerateAIRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ReadableStream<Uint8Array>> => {
    const response = await fetch(`${API_URL}/api/ai/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: options?.signal,
    });

    if (!response.ok) {
      const message = await buildFriendlyFetchErrorMessage(response, 'AI stream request failed');
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('AI stream response body is empty');
    }

    return response.body;
  },

  streamStructuredV2: async (
    request: StructuredFieldsV2Request,
    options?: { signal?: AbortSignal }
  ): Promise<ReadableStream<Uint8Array>> => {
    const response = await fetch(`${API_URL}/api/ai/stream-structured-v2`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: options?.signal,
    });

    if (!response.ok) {
      const message = await buildFriendlyFetchErrorMessage(response, 'AI structured stream v2 request failed');
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('AI structured stream v2 response body is empty');
    }

    return response.body;
  },

  streamFieldExtractionV2: async (
    request: ExtractFieldBatchRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ReadableStream<Uint8Array>> => {
    const parsedPayload = extractFieldBatchRequestSchema.parse(request);
    const response = await fetch(`${API_URL}/api/ai/stream-field-extraction-v2`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(parsedPayload),
      signal: options?.signal,
    });

    if (!response.ok) {
      const message = await buildFriendlyFetchErrorMessage(response, 'AI field extraction stream v2 request failed');
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('AI field extraction stream v2 response body is empty');
    }

    return response.body;
  },

  streamFinalAnalysisV1: async (
    request: AnalysisBatchRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ReadableStream<Uint8Array>> => {
    const parsedPayload = analysisBatchRequestSchema.parse(request);
    const response = await fetch(`${API_URL}/api/ai/stream-final-analysis-v1`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(parsedPayload),
      signal: options?.signal,
    });

    if (!response.ok) {
      const message = await buildFriendlyFetchErrorMessage(response, 'AI final analysis stream v1 request failed');
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('AI final analysis stream v1 response body is empty');
    }

    return response.body;
  },

  getHistory: async (limit?: number) => {
    const response = await apiClient.get('/api/ai/history', {
      params: { limit },
    });
    return response.data;
  },
};

export const projectTriageTemplatesApi = {
  create: async (
    projectId: string,
    data: CreateTriageFieldTemplateRequest
  ): Promise<TriageFieldTemplate> => {
    const parsedPayload = createTriageFieldTemplateSchema.parse(data);
    const response = await apiClient.post(`/api/projects/${projectId}/triage-field-templates`, parsedPayload);
    return triageFieldTemplateSchema.parse(response.data);
  },

  getLatest: async (projectId: string): Promise<TriageFieldTemplate | null> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-field-templates/latest`);
    if (response.data == null) {
      return null;
    }
    return triageFieldTemplateSchema.parse(response.data);
  },

  list: async (projectId: string, limit = 20): Promise<TriageFieldTemplate[]> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-field-templates`, {
      params: { limit },
    });
    return triageFieldTemplateSchema.array().parse(response.data);
  },

  getById: async (projectId: string, templateId: string): Promise<TriageFieldTemplate> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-field-templates/${templateId}`);
    return triageFieldTemplateSchema.parse(response.data);
  },

  update: async (
    projectId: string,
    templateId: string,
    data: UpdateTriageFieldTemplateRequest
  ): Promise<TriageFieldTemplate> => {
    const parsedPayload = updateTriageFieldTemplateSchema.parse(data);
    const response = await apiClient.put(`/api/projects/${projectId}/triage-field-templates/${templateId}`, parsedPayload);
    return triageFieldTemplateSchema.parse(response.data);
  },

  delete: async (projectId: string, templateId: string): Promise<void> => {
    await apiClient.delete(`/api/projects/${projectId}/triage-field-templates/${templateId}`);
  },
};

export const userTriageTemplatesApi = {
  list: async (query: Partial<ListUserTriageTemplatesQuery> = {}): Promise<ListUserTriageTemplatesResponse> => {
    const parsedQuery = listUserTriageTemplatesQuerySchema.partial().parse(query);
    const response = await apiClient.get('/api/triage-field-templates', {
      params: parsedQuery,
    });
    return listUserTriageTemplatesResponseSchema.parse(response.data);
  },

  getById: async (templateId: string): Promise<UserTriageTemplateDetail> => {
    const response = await apiClient.get(`/api/triage-field-templates/${templateId}`);
    return userTriageTemplateDetailSchema.parse(response.data);
  },

  update: async (templateId: string, data: UpdateTriageFieldTemplateRequest): Promise<UserTriageTemplateDetail> => {
    const parsedPayload = updateTriageFieldTemplateSchema.parse(data);
    const response = await apiClient.put(`/api/triage-field-templates/${templateId}`, parsedPayload);
    return userTriageTemplateDetailSchema.parse(response.data);
  },

  delete: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/api/triage-field-templates/${templateId}`);
  },
};

export const projectExtractionBatchesApi = {
  getLatest: async (projectId: string): Promise<ExtractFieldBatchDetail | null> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-extraction-batches/latest`);
    if (response.data == null) {
      return null;
    }
    return extractFieldBatchDetailSchema.parse(response.data);
  },

  list: async (projectId: string, limit = 20, offset = 0): Promise<ExtractFieldBatchDetail[]> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-extraction-batches`, {
      params: { limit, offset },
    });
    return extractFieldBatchDetailSchema.array().parse(response.data);
  },

  update: async (
    projectId: string,
    batchId: string,
    data: UpdateTriageExtractionBatchRequest
  ): Promise<ExtractFieldBatchDetail> => {
    const parsedPayload = updateTriageExtractionBatchSchema.parse(data);
    const response = await apiClient.put(`/api/projects/${projectId}/triage-extraction-batches/${batchId}`, parsedPayload);
    return extractFieldBatchDetailSchema.parse(response.data);
  },

  delete: async (projectId: string, batchId: string): Promise<void> => {
    await apiClient.delete(`/api/projects/${projectId}/triage-extraction-batches/${batchId}`);
  },

  saveRecordReview: async (
    projectId: string,
    batchId: string,
    recordId: string,
    payload: SaveRecordReviewRequest
  ): Promise<SaveRecordReviewResponse> => {
    const parsedPayload = saveRecordReviewRequestSchema.parse(payload);
    const response = await apiClient.put(
      `/api/projects/${projectId}/triage-extraction-batches/${batchId}/reviews/${recordId}`,
      parsedPayload
    );
    return saveRecordReviewResponseSchema.parse(response.data);
  },

  createSnapshot: async (
    projectId: string,
    payload: CreateTriageExtractionSnapshotRequest
  ): Promise<CreateTriageExtractionSnapshotResponse> => {
    const parsedPayload = createTriageExtractionSnapshotSchema.parse(payload);
    const response = await apiClient.post(`/api/projects/${projectId}/triage-extraction-batches/snapshots`, parsedPayload);
    return createTriageExtractionSnapshotResponseSchema.parse(response.data);
  },
};

export const projectAnalysisBatchesApi = {
  getLatest: async (projectId: string): Promise<AnalysisBatchDetail | null> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-analysis-batches/latest`);
    if (response.data == null) {
      return null;
    }
    return analysisBatchDetailSchema.parse(response.data);
  },

  list: async (projectId: string, limit = 20, offset = 0): Promise<AnalysisBatchDetail[]> => {
    const response = await apiClient.get(`/api/projects/${projectId}/triage-analysis-batches`, {
      params: { limit, offset },
    });
    return analysisBatchDetailSchema.array().parse(response.data);
  },

  delete: async (projectId: string, batchId: string): Promise<void> => {
    await apiClient.delete(`/api/projects/${projectId}/triage-analysis-batches/${batchId}`);
  },

  createSnapshot: async (
    projectId: string,
    payload: CreateTriageAnalysisSnapshotRequest
  ): Promise<CreateTriageAnalysisSnapshotResponse> => {
    const parsedPayload = createTriageAnalysisSnapshotSchema.parse(payload);
    const response = await apiClient.post(`/api/projects/${projectId}/triage-analysis-batches/snapshots`, parsedPayload);
    return createTriageAnalysisSnapshotResponseSchema.parse(response.data);
  },

  append: async (
    projectId: string,
    batchId: string,
    payload: AppendTriageAnalysisBatchRequest
  ): Promise<AppendTriageAnalysisBatchResponse> => {
    const parsedPayload = appendTriageAnalysisBatchSchema.parse(payload);
    const response = await apiClient.post(`/api/projects/${projectId}/triage-analysis-batches/${batchId}/append`, parsedPayload);
    return appendTriageAnalysisBatchResponseSchema.parse(response.data);
  },

  probeAppendCapability: async (
    projectId: string,
    options?: { signal?: AbortSignal }
  ): Promise<{ supported: boolean; status: number; code: string; message: string }> => {
    const response = await fetch(`${API_URL}/api/projects/${projectId}/triage-analysis-batches/append-capability`, {
      method: 'GET',
      credentials: 'include',
      signal: options?.signal,
    });

    const raw = await response.text().catch(() => '');
    let payload: ReturnType<typeof toApiPayload> = null;
    if (raw) {
      try {
        payload = toApiPayload(JSON.parse(raw));
      } catch {
        payload = null;
      }
    }

    const code = typeof payload?.code === 'string' ? payload.code.trim() : '';
    const message = typeof payload?.error === 'string' ? payload.error.trim() : '';
    const supported = response.ok;

    return {
      supported,
      status: response.status,
      code,
      message,
    };
  },
};

export function validateFieldDecisionCards(input: unknown): FieldDecisionCard[] {
  return fieldDecisionCardSchema.array().parse(input);
}

export function validateAbstractExtractionResult(input: unknown): AbstractExtractionResult {
  return abstractExtractionResultSchema.parse(input);
}

// Records API
export const recordsApi = {
  list: async (query: Partial<ListQuery> & { includeTotal?: boolean } = {}): Promise<ListResponse> => {
    const response = await apiClient.get('/api/records', { params: query });
    return listResponseSchema.parse(response.data);
  },

  listPublicationTypeFacets: async (
    query: Pick<ListQuery, 'projectId' | 'searchId' | 'searchIds' | 'textQuery' | 'minImpactFactor'> = {}
  ): Promise<string[]> => {
    const response = await apiClient.get('/api/records/facets/publication-types', { params: query });
    return recordPublicationTypeFacetsResponseSchema.parse(response.data).items;
  },

  get: async (id: string): Promise<RecordType> => {
    const response = await apiClient.get(`/api/records/${id}`);
    return recordSchema.parse(response.data);
  },

  create: async (data: CreateRecordRequest): Promise<RecordType> => {
    const response = await apiClient.post('/api/records', data);
    return recordSchema.parse(response.data);
  },

  update: async (id: string, data: UpdateRecordRequest): Promise<RecordType> => {
    const response = await apiClient.put(`/api/records/${id}`, data);
    return recordSchema.parse(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/records/${id}`);
  },
};

export default apiClient;
